/**
 * Google / Apple / GitHub のログイン・連携・再認証（2026-09-27 creator/dev-oauth-ui）の英語の文言。en.ts が `...enOAuth` で取り込む
 * （ja.oauth.ts が同じキーを全部持つことを型で強制）。
 *
 * 使う所: components/OAuthButtons.tsx（ボタン）・pages/OAuthCallback.tsx（戻り・takeover の案内）・pages/AuthPage.tsx（戻りの失敗の案内）・
 * components/Reauth.tsx（再認証のダイアログ）・pages/AccountLoginMethods.tsx（/account の「ログイン方法」）・pages/AccountPage.tsx（閉鎖の再認証）。
 *
 * 決め事:
 *   - プロバイダ名は {provider}（Google / Apple / GitHub。ブランド名なので訳さない）
 *   - 同じメールで自動に紐づけない（サーバーの resolve.ts・管理判断 2026-09-27）。email_exists では「いつもの方法で入ってから設定で追加」を必ず書く
 *   - 外す・パスワードの設定では「ほかの端末と接続中のツールはログアウト」、パスワードの設定では「live の出金の確認が 72 時間止まる」を必ず書く
 */
export const enOAuth = {
  'oauth.or': 'or',
  'oauth.continueWith': 'Continue with {provider}',
  'oauth.callback.working': 'Signing you in…',
  'oauth.callback.invalidTitle': 'Sign-in did not finish',

  // 戻りのエラー（サーバーの DevOAuthError と invalid_state）
  'oauth.error.access_denied': 'Sign-in with {provider} was cancelled.',
  'oauth.error.provider_error': 'Could not sign in with {provider}. Please try again in a moment.',
  'oauth.error.email_exists':
    'An account with this email already exists. Log in the usual way (for example with your password), then add {provider} under "Sign-in methods" in your account settings.',
  'oauth.error.verify_email_first':
    'This email\'s account needs to be confirmed first. Use "Forgot password?" to confirm the email and log in, then add {provider} in your account settings.',
  'oauth.error.email_not_verified': 'Your {provider} account has no confirmed email address, so it cannot be used to create a {product} account. Confirm the email with {provider}, or sign up with an email and password.',
  // 2026-10-29 core/open-signup-build（サーバーの 400 disposable_email・招待なしの間の新規登録だけ）
  'oauth.error.disposable_email': 'The email on your {provider} account is a disposable address, so it cannot be used to create a {product} account. Use an account with an email address you keep.',
  'oauth.error.identity_already_linked': 'This {provider} account is already used by a different {product} account.',
  'oauth.error.provider_already_linked': 'A different {provider} account is already added. Remove it first to add another one.',
  'oauth.error.reauth_mismatch': 'That {provider} account is not the one added to this {product} account. Choose the same account you added.',
  'oauth.error.invalid_state': 'This sign-in link expired or was started in another tab. Please start again.',
  'oauth.error.redirectNotAllowed': 'Sign-in with this provider is not set up for this dashboard address. Ask the server operator to check KUMO_DASHBOARD_URL.',

  'oauth.takeover.title': 'You are now signed in with this email',
  'oauth.takeover.body':
    'For safety, the old password no longer works, and the dashboard on other devices and any AI agent or terminal command that was connected were signed out. Server-only keys (sk_) no longer work either: make new ones on the “Keys (pk_ / sk_)” page, or ask your AI to “set up the keys”.',
  'oauth.takeover.hold': 'Passkeys were removed too — add yours again to withdraw. For safety, confirming live withdrawals is paused for 72 hours.',

  // 再認証のダイアログ（共通の部品）
  'reauth.title': 'Confirm it is you',
  'reauth.lead': 'This is an important change, so please confirm it is really you.',
  'reauth.failed': 'Could not confirm — the password was wrong or the confirmation expired. Try again.',
  'reauth.password': 'Current password',
  'reauth.confirm': 'Confirm',
  'reauth.confirmWith': 'Confirm with {provider}',
  'reauth.oauthNote': 'You will come back here after confirming. The confirmation is valid for 5 minutes and can be used once.',
  'reauth.noMethod': 'There is no way to confirm on this account right now. Use "Forgot password?" on the log-in page to set a password.',

  // 閉鎖のフォーム（パスワードを持たない人・Google 等から戻ったとき）
  'account.close.oauthConfirmNote': 'You signed up with Google or GitHub. After you press the button, confirm with that account.',
  'account.close.resumedTitle': 'Confirmed. Close the account now?',
  'account.close.resumedBody': 'This cannot be undone. The confirmation is valid for 5 minutes.',

  // /account の「ログイン方法」
  'login.title': 'Sign-in methods',
  'login.lead': 'Ways you can log in to this account.',
  'login.password': 'Password',
  'login.passwordSet': 'Set',
  'login.passwordNotSet': 'Not set',
  'login.setPassword': 'Set a password',
  'login.changePassword': 'Change password',
  'login.notLinked': 'Not added',
  'login.add': 'Add {provider}',
  'login.remove': 'Remove',
  'login.addedOn': 'added {date}',
  'login.lastUsed': 'last used {date}',
  'login.linked': '{provider} was added. You can now log in with it.',
  'login.unlinked': '{provider} was removed.',
  'login.lastMethod': 'The last way to sign in cannot be removed. Set a password or add another method first.',
  'login.noAutoLink':
    '{product} never connects a Google or GitHub account to yours just because the email matches — you add it here yourself, after logging in.',
  'login.removeTitle': 'Remove {provider}?',
  'login.removeBody': 'You will no longer be able to log in with {provider}.',
  'login.signOutOthers': 'The dashboard on your other devices, and any AI agent or terminal command you connected, will be signed out. This browser stays signed in.',
  'login.withdrawHold': 'For safety, confirming live withdrawals is paused for 72 hours after setting or changing the password.',
  'login.setPasswordTitle': 'Set a password',
  'login.changePasswordTitle': 'Change password',
  'login.currentPassword': 'Current password',
  'login.currentRequired': 'Enter your current password.',
  'login.confirmedWith': 'Confirmed with {provider}.',
  'login.reauthExpired': 'The confirmation expired. Close this and start again.',
  'login.savePassword': 'Save password',
  'login.passwordSaved': 'Password saved. The dashboard on your other devices, and any AI agent or terminal command you connected, were signed out.',

  // サーバーの error.code（errorText が `error.<code>` を自動で引く）
  'error.provider_not_configured': 'This sign-in method is not available on this server.',
  'error.reauthentication_required': 'Please confirm it is you to continue.',
  'error.last_login_method': 'This is the last way to sign in to this account. Set a password or add another sign-in method first.'
};
